// frontend/src/components/OfdCredentialsPage.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  IconButton,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const OfdCredentialsPage = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCredential, setCurrentCredential] = useState(null);
  const [formData, setFormData] = useState({
    client_name: '',
	inn: '',
	ofd: '',
    login_ofd: '',
    password_ofd: '',
    agreement_number: '',
    lm4z_token: '',
    dreamkas_kabinet_token: ''
  });

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/ofd_credentials/');
      setCredentials(response.data);
    } catch (err) {
      setError('Ошибка при загрузке учетных данных');
      console.error('Ошибка:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (credential = null) => {
    if (credential) {
      setCurrentCredential(credential);
      setFormData({
        client_name: credential.client_name || '',
        login_ofd: credential.login_ofd || '',
        password_ofd: credential.password_ofd || '',
        agreement_number: credential.agreement_number || '',
        lm4z_token: credential.lm4z_token || '',
        dreamkas_kabinet_token: credential.dreamkas_kabinet_token || ''
      });
    } else {
      setCurrentCredential(null);
      setFormData({
        client_name: '',
		inn: '',
		ofd: '',
        login_ofd: '',
        password_ofd: '',
        agreement_number: '',
        lm4z_token: '',
        dreamkas_kabinet_token: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCredential(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentCredential) {
        // Обновление
        await axios.put(`/ofd_credentials/${currentCredential.id}`, formData);
      } else {
        // Создание
        await axios.post('/ofd_credentials/', formData);
      }
      handleCloseDialog();
      fetchCredentials();
    } catch (err) {
      setError('Ошибка при сохранении данных');
      console.error('Ошибка:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эти учетные данные?')) {
      try {
        await axios.delete(`/ofd_credentials/${id}`);
        fetchCredentials();
      } catch (err) {
        setError('Ошибка при удалении данных');
        console.error('Ошибка:', err);
      }
    }
  };

  return (
    <Container maxWidth="lg" style={{ marginTop: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Учетные данные ОФД</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Добавить учетные данные
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} style={{ marginBottom: '20px' }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Клиент</TableCell>
			  <TableCell>ИНН</TableCell>
			  <TableCell>ОФД</TableCell>
              <TableCell>Логин ОФД</TableCell>
              <TableCell>Номер договора</TableCell>
              <TableCell>ЛМЧЗ токен</TableCell>
              <TableCell>Дримкас токен</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {credentials.map((credential) => (
              <TableRow key={credential.id}>
                <TableCell>{credential.client_name}</TableCell>
				<TableCell>{credential.inn}</TableCell>
				<TableCell>{credential.ofd}</TableCell>
                <TableCell>{credential.login_ofd}</TableCell>
                <TableCell>{credential.agreement_number || '-'}</TableCell>
                <TableCell>{credential.lm4z_token ? 'Да' : 'Нет'}</TableCell>
                <TableCell>{credential.dreamkas_kabinet_token ? 'Да' : 'Нет'}</TableCell>
                <TableCell>
                  <IconButton 
                    color="primary" 
                    onClick={() => handleOpenDialog(credential)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    color="secondary" 
                    onClick={() => handleDelete(credential.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentCredential ? 'Редактировать учетные данные' : 'Добавить учетные данные'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="client_name"
              label="Название клиента"
              name="client_name"
              value={formData.client_name}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="inn"
              label="ИНН"
              name="inn"
              value={formData.inn}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="ofd"
              label="ОФД"
              name="ofd"
              value={formData.ofd}
              onChange={handleInputChange}
            />			
            <TextField
              margin="normal"
              required
              fullWidth
              id="login_ofd"
              label="Логин ОФД"
              name="login_ofd"
              value={formData.login_ofd}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="password_ofd"
              label="Пароль ОФД"
              name="password_ofd"
              type="password"
              value={formData.password_ofd}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              id="agreement_number"
              label="Номер договора"
              name="agreement_number"
              value={formData.agreement_number}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              id="lm4z_token"
              label="LM4Z токен"
              name="lm4z_token"
              value={formData.lm4z_token}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              id="dreamkas_kabinet_token"
              label="Токен кабинета Дримкас"
              name="dreamkas_kabinet_token"
              value={formData.dreamkas_kabinet_token}
              onChange={handleInputChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OfdCredentialsPage;
